import PropTypes from 'prop-types'
import {useCallback, useEffect, useState} from 'react'
import {connect} from 'react-redux'
import {List, Map} from 'immutable'
import {useMediaQuery} from '@mui/material'
import {readEndpoint, updateResource} from 'redux-json-api'

import Button from '../../../../shared_components/mui_base_components/button/Button'
import DeathReporterReminderPrompt from './DeathReporterReminderPrompt'
import MyDeputies from './MyDeputies'
import MyDraftDeputies from './MyDraftDeputies'
import NoDeputiesModule from './NoDeputiesModule'
import Logger from '../../../../lib/NewLogger'
import MixpanelLogger from '../../../../lib/MixpanelLogger'
import SuggestedDeputiesCarousel from './SuggestedDeputiesCarousel'
import routerUtils from '../../../../lib/routerUtils'

import {
  hasDeputyWithDeferredPermissions
} from '../../../../lib/deputyPermissionTools'

import {currentUserConfig} from '../../../../lib/userTools'
import {scrollToTopOfPage} from '../../../../lib/scrollTools'
import {useEverplanData, useDeputyData} from '../../../../lib/hooks'

import {
  findRawResourceByAttributeId,
  findRawResourceById
} from '../../../../lib/plan_data/userData'

import {cleanResponse} from '../corp_max/redux/apiHelper'

import './myDeputiesController.scss'

const MyDeputiesController = props => {
  const context = 'my_deputies'
  const everplanId = props.userConfig.get('everplan-id')

  const {deputies} = useDeputyData(everplanId)
  const {itemResponses, newContacts} = useEverplanData(everplanId)

  const isTabletMdOrAbove = useMediaQuery(theme => theme.breakpoints.up('tabletMd'))

  const [draftDeputies, setDraftDeputies] = useState(List())

  const logViewEvent = () => {
    const payload = {}
    const query_context = props.location.query.context

    if (query_context)
      payload.context = query_context

    MixpanelLogger.track('view_my_deputies', payload)
  }

  // Fetch draftDeputies directly and manage in local state because after review
  // in the frontend they are deleted in the backend, leaving Redux state stale.
  const fetchDraftDeputies = useCallback(() => {
    props.readEndpoint(`everplans/${everplanId}?include=draft-deputies`)
      .then(response => {
        const includedDraftDeputies = response.body.included?.filter(
          includedResource => includedResource.type === 'draft-deputies'
        ) || []

        const updatedDraftDeputies = cleanResponse({
          data: includedDraftDeputies
        })

        setDraftDeputies(updatedDraftDeputies || List())
      })
  }, [everplanId, props.readEndpoint])

  useEffect(() => {
    fetchDraftDeputies()
    logViewEvent()
    scrollToTopOfPage()
  }, [])

  const transformDraftDeputyPermissions = draftDeputy => {
    const permissionsConfig = draftDeputy.get('permissions-config', Map())
    const readPermissions = []
    const deferredPermissions = []

    permissionsConfig.forEach((value, key) => {
      if (key !== 'selectAll') {
        const permissionType = value.get('value')
        if (permissionType === 'read')
          readPermissions.push(value)
        else if (permissionType === 'deferred_read')
          deferredPermissions.push(value)
      }
    })

    return draftDeputy.setIn(
      ['permissions'],
      Map({
        read: List(readPermissions),
        deferred: List(deferredPermissions)
      })
    )
  }

  const goToAddDeputy = () => {
    MixpanelLogger.track(
      'click_add_deputy',
      {context, deputy_count: deputies.size, source: 'planholder'}
    )

    Logger.log({
      name: 'clicked_add_new_deputy',
      payload: {context}
    })

    routerUtils.push('cm/deputies/new')
  }

  const exit = () => { routerUtils.push('cm/deputies') }

  const handleDraftDeputyRemoved = useCallback(() => {
    fetchDraftDeputies()
  }, [fetchDraftDeputies])

  const handleDraftDeputyReviewed = useCallback(() => {
    fetchDraftDeputies()
  }, [fetchDraftDeputies])

  const transformedDraftDeputies = draftDeputies.map(
    transformDraftDeputyPermissions
  )

  const allDeputies = deputies.concat(transformedDraftDeputies)

  const hasDeputyWithDeferredItems = hasDeputyWithDeferredPermissions(
    allDeputies
  )

  const sortedDeputies = deputies.groupBy(deputy => (
    deputy.getIn(['ownable', 'status']) === 'confirmed' ?
      'confirmed' :
      'pending'
  )).reduce((result, group, key) => (
    key === 'confirmed' ? result.concat(group) : result
  ), List()).concat(
    deputies.groupBy(deputy => (
      deputy.getIn(['ownable', 'status']) === 'confirmed' ?
        'confirmed' :
        'pending'
    )).get('pending', List())
  )

  return (
    <div className='my-deputies-controller'>
      <DeathReporterReminderPrompt
        hasDeputyWithDeferredItems={hasDeputyWithDeferredItems}
        deputies={allDeputies}
      />
      <div className='my-deputies-controller__header'>
        <h1 className='my-deputies-controller__title'>My Deputies</h1>
        <Button
          className='my-deputies-controller__add-deputy-button'
          onClick={goToAddDeputy}>
          {isTabletMdOrAbove ? 'Invite someone' : 'Invite'}
        </Button>
      </div>
      <SuggestedDeputiesCarousel
        deputies={deputies}
        everplanId={everplanId}
        location={props.location || {pathname: ''}}
        context={context}
      />
      {
        deputies.isEmpty() ?
          <>
            <MyDraftDeputies
              context={context}
              deputies={deputies}
              draftDeputies={transformedDraftDeputies}
              hasDeputyWithDeferredItems={hasDeputyWithDeferredItems}
              itemResponses={itemResponses}
              newContacts={newContacts}
              onDraftDeputyRemoved={handleDraftDeputyRemoved}
              onDraftDeputyReviewed={handleDraftDeputyReviewed}
              userConfig={props.userConfig}
            />
            <NoDeputiesModule
              currentUser={props.currentUser}
              updateResource={props.updateResource}
              goToAddDeputy={goToAddDeputy}
            />
          </> :
          <>
            <MyDeputies
              {...props}
              deputies={sortedDeputies}
              goToAddDeputy={goToAddDeputy}
              hasDeputyWithDeferredItems={hasDeputyWithDeferredItems}
            />
            <MyDraftDeputies
              context={context}
              deputies={sortedDeputies}
              draftDeputies={transformedDraftDeputies}
              hasDeputyWithDeferredItems={hasDeputyWithDeferredItems}
              itemResponses={itemResponses}
              newContacts={newContacts}
              onDraftDeputyRemoved={handleDraftDeputyRemoved}
              onDraftDeputyReviewed={handleDraftDeputyReviewed}
              userConfig={props.userConfig}
            />
          </>
      }
      {
        routerUtils.childrenWithProps(
          props.children,
          {
            ...props,
            context,
            exit,
            itemResponses,
            deputies,
            newContacts
          }
        )
      }
    </div>
  )
}

MyDeputiesController.propTypes = {
  currentUser: PropTypes.instanceOf(Map),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.object
  }),
  readEndpoint: PropTypes.func,
  updateResource: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map),
  vaultExtension: PropTypes.instanceOf(Map)
}

const mapStateToProps = ({categories, api}) => {
  const userConfig = currentUserConfig(api)

  const currentUser = findRawResourceById({
    rawResource: api.users, id: userConfig.get('id')
  })

  const vaultExtension = findRawResourceByAttributeId({
    rawResource: api['vault-extensions'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })

  return ({
    categories,
    currentUser,
    userConfig,
    vaultExtension
  })
}

export default connect(mapStateToProps, {readEndpoint, updateResource})(MyDeputiesController)
